import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { isElementPartiallyInViewport } from './viewport';

let hasScrollStarted = false;
let firstScrollPromise = null;

function markScrollStarted() {
  hasScrollStarted = true;
}

function getHasScrollStarted() {
  if (hasScrollStarted) return true;

  const scrollTop =
    window.scrollY ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0;

  if (scrollTop > 0) {
    markScrollStarted();
  }

  return hasScrollStarted;
}

/**
 * Resolves after the first user scroll intent.
 *
 * The gate listens to wheel, touchmove, native scroll, and common scroll keys.
 * If the page already has a positive scroll offset, it resolves immediately so
 * lazy loads encountered later do not wait for a second scroll action.
 */
export function waitForFirstScroll() {
  if (getHasScrollStarted()) return Promise.resolve();

  if (!firstScrollPromise) {
    firstScrollPromise = new Promise((resolve) => {
      const onScrollStart = () => {
        markScrollStarted();
        cleanup();
        resolve();
      };

      const onScrollKeyDown = (event) => {
        const scrollKeys = [
          ' ',
          'ArrowDown',
          'ArrowUp',
          'End',
          'Home',
          'PageDown',
          'PageUp',
        ];

        if (scrollKeys.includes(event.key)) {
          onScrollStart();
        }
      };

      const cleanup = () => {
        window.removeEventListener('scroll', onScrollStart);
        window.removeEventListener('wheel', onScrollStart);
        window.removeEventListener('touchmove', onScrollStart);
        window.removeEventListener('keydown', onScrollKeyDown);
      };

      window.addEventListener('scroll', onScrollStart, { passive: true });
      window.addEventListener('wheel', onScrollStart, { passive: true });
      window.addEventListener('touchmove', onScrollStart, { passive: true });
      window.addEventListener('keydown', onScrollKeyDown);
    });
  }

  return firstScrollPromise;
}

/**
 * Resolves when lazy loading is allowed for a trigger element.
 *
 * Directly visible elements may load immediately. Elements that are only inside
 * the preload zone on initial page load wait until the first scroll intent.
 */
export function waitForLazyLoadGate(trigger) {
  if (isElementPartiallyInViewport(trigger) || getHasScrollStarted()) {
    return Promise.resolve();
  }

  return waitForFirstScroll();
}

/**
 * Creates a ScrollTrigger-backed lazy-load gate.
 *
 * `onLoad` runs once the trigger enters the preload zone and the shared lazy
 * load gate permits loading. The default start mirrors the project convention:
 * begin preparing work when the trigger is within one viewport below the fold,
 * unless it was already in that preload zone during the initial page load.
 */
export function createLazyLoadTrigger({
  trigger,
  start = 'top bottom+=100%',
  once = true,
  onLoad,
  onError,
  onFinally,
} = {}) {
  if (!trigger || typeof onLoad !== 'function') return null;

  return ScrollTrigger.create({
    trigger,
    start,
    once,
    onEnter: (self) => {
      waitForLazyLoadGate(trigger)
        .then(() => onLoad(self))
        .catch((err) => {
          if (typeof onError === 'function') {
            onError(err, self);
          } else {
            throw err;
          }
        })
        .finally(() => {
          if (once) self.kill();
          if (typeof onFinally === 'function') onFinally(self);
        });
    },
  });
}
